import { useCallback, useEffect } from 'react'
import { useInView } from 'react-intersection-observer'
import type { LoyaltyCreditCardPlacement } from 'analytics/snowplow/events/loyaltyCreditCardEvents'
import {
  trackLoyaltyCreditCardClick,
  trackLoyaltyCreditCardImpression,
} from 'analytics/snowplow/events/loyaltyCreditCardEvents'

/**
 * Impression and click tracking for one Loyalty Credit Card placement.
 *
 * Put `impressionRef` on whatever element counts as "seen" and pass `onPlacementClick` to the
 * link. Every placement shares this, so a new surface cannot accidentally report a different shape
 * from the others.
 */
function useLoyaltyCreditCardPlacementTracking(placement: LoyaltyCreditCardPlacement) {
  // Half the element visible, once per mount — a banner that merely clipped the viewport edge as
  // the customer scrolled past has not really been seen.
  const [impressionRef, isInView] = useInView({ threshold: 0.5, triggerOnce: true })

  useEffect(() => {
    if (isInView) {
      trackLoyaltyCreditCardImpression(placement)
    }
  }, [isInView, placement])

  const onPlacementClick = useCallback(() => {
    trackLoyaltyCreditCardClick(placement)
  }, [placement])

  return { impressionRef, onPlacementClick }
}

export default useLoyaltyCreditCardPlacementTracking
